import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Copy } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useLang } from "../../context/LangContext";
import { API } from "../../utils/format";
import { PercentInput } from "../../components/ui/PercentInput";
import { FieldHint } from "../../components/ui/FieldHint";
import QuickPricelistDialog from "./QuickPricelistDialog";
import { Zap } from "lucide-react";



const PricelistTypesManagement = () => {
  const { t } = useLang();
  const [types, setTypes] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editType, setEditType] = useState(null);
  const [quick, setQuick] = useState(null); // null = chiuso, false = nuovo, oggetto = modifica
  const [formData, setFormData] = useState({ name: "", type: "agent", commission: 0, markup: 0, discount: 0, notes: "" });

  const fetchTypes = async () => {
    try {
      const response = await axios.get(`${API}/pricelist-types`);
      setTypes(response.data);
    } catch (error) {
      try {
        await axios.post(`${API}/init-pricelist-types`);
        const response = await axios.get(`${API}/pricelist-types`);
        setTypes(response.data);
      } catch { toast.error("Errore nel caricamento"); }
    }
  };

  useEffect(() => { fetchTypes(); }, []);

  const handleSave = async () => {
    try {
      if (editType?.id) await axios.put(`${API}/pricelist-types/${editType.id}`, formData);
      else await axios.post(`${API}/pricelist-types`, formData);
      toast.success("Salvato");
      setIsDialogOpen(false);
      setEditType(null);
      setFormData({ name: "", type: "agent", commission: 0, markup: 0, discount: 0, notes: "" });
      fetchTypes();
    } catch { toast.error("Errore"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    await axios.delete(`${API}/pricelist-types/${id}`);
    toast.success("Eliminato");
    fetchTypes();
  };

  const handleDuplicate = async (type) => {
    const name = window.prompt("Nome del nuovo listino", `Copia di ${type.name}`);
    if (!name) return;
    try {
      const r = await axios.post(`${API}/pricelist-types/${type.id}/duplicate`, { name });
      toast.success(`Listino "${r.data.name}" creato: ${r.data.copied_products} articoli con prezzi/provvigioni copiati`);
      fetchTypes();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore duplicazione"); }
  };

  const openEdit = (type) => {
    setEditType(type);
    setFormData({
      name: type?.name || "", type: type?.type || "agent",
      commission: type?.commission || 0, markup: type?.markup || 0,
      discount: type?.discount || 0,
      notes: type?.notes || "",
    });
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-display font-semibold text-stone-900">{t.pricelists}</h2>
          <p className="text-stone-500 text-sm mt-1">Configura tipi listino (Distributori, Direzionali, Agenti) o crea un listino rapido con solo gli articoli che servono</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setQuick(false)} variant="outline" className="border-[#C5A059] text-[#8A1C2A] hover:bg-[#C5A059]/10" data-testid="add-quick-pricelist-btn">
            <Zap className="w-4 h-4 mr-2" />Listino rapido
          </Button>
          <Button onClick={() => openEdit(null)} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="add-pricelist-type-btn">
            <Plus className="w-4 h-4 mr-2" />{t.new}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {types.map(type => (
          <Card key={type.id} className="bg-white border-stone-200 hover:border-[#C5A059] hover:shadow-md transition-all">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-stone-900">{type.name}</h3>
                  <Badge className={`mt-1 ${type.type === 'distributor' ? 'bg-blue-100 text-blue-700' : type.type === 'directional' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                    {type.type === 'distributor' ? 'Distributore' : type.type === 'directional' ? 'Direzionale' : 'Agente'}
                  </Badge>
                  {type.active_count != null && (
                    <button type="button" onClick={() => setQuick(type)} className="ml-1 mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#C5A059]/20 text-[#8A1C2A] text-[11px] font-semibold hover:bg-[#C5A059]/40" title="Articoli attivi nel listino: clicca per scegliere articoli, prezzi e provvigioni" data-testid={`quick-badge-${type.id}`}>
                      {type.quick && <Zap className="w-3 h-3" />}{type.active_count} articoli
                    </button>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setQuick(type)} className="h-8 w-8 p-0 hover:bg-[#C5A059]/20" title="Articoli, prezzi e provvigioni del listino" data-testid={`quick-edit-${type.id}`}><Zap className="w-4 h-4 text-[#C5A059]" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDuplicate(type)} className="h-8 w-8 p-0 hover:bg-blue-50" title="Duplica listino (articoli, prezzi fissi, provvigioni e sconti compresi)" data-testid={`duplicate-pricelist-${type.id}`}><Copy className="w-4 h-4 text-blue-600" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(type)} className="h-8 w-8 p-0 hover:bg-stone-100" data-testid={`edit-pricelist-${type.id}`}><Edit className="w-4 h-4 text-stone-500" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(type.id)} className="h-8 w-8 p-0 hover:bg-red-50" data-testid={`delete-pricelist-${type.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-stone-500">{t.commission}:</span><span className="text-[#8A1C2A] font-semibold">{(type.commission * 100).toFixed(1)}%</span></div>
                <div className="flex justify-between"><span className="text-stone-500">{t.markup}:</span><span className="text-stone-900 font-medium">{(type.markup * 100).toFixed(1)}%</span></div>
                {type.discount > 0 && (
                  <div className="flex justify-between"><span className="text-stone-500">🏷️ Sconto:</span><span className="text-[#C5A059] font-bold">−{(type.discount * 100).toFixed(1)}%</span></div>
                )}
                {type.last_published_at && (
                  <div className="text-xs text-stone-400 pt-1 border-t border-stone-100 mt-2">
                    📌 Pubblicato: {new Date(type.last_published_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}
                  </div>
                )}
                {type.notes && (
                  <div className="mt-3 pt-3 border-t border-stone-200">
                    <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">📝 Note</p>
                    <p className="text-xs text-stone-600 italic whitespace-pre-wrap">{type.notes}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-white border-stone-200">
          <DialogHeader><DialogTitle className="text-stone-900">{editType?.id ? t.edit : t.new} Tipo Listino</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-stone-700">Nome</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="es. Agente Marco" className="bg-white border-stone-300" data-testid="pricelist-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-stone-700">{t.type}</Label>
              <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                <SelectTrigger className="bg-white border-stone-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="distributor">Distributore</SelectItem>
                  <SelectItem value="directional">Direzionale</SelectItem>
                  <SelectItem value="agent">Agente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-stone-700 flex items-center gap-1">
                  {t.commission}
                  <FieldHint>
                    <strong>Commissione base del listino</strong> assegnata ad un agente sulle vendite. È un valore di <em>default</em>: può essere sovrascritto a livello di Agente, Cliente o Articolo (priorità: Cliente &gt; Articolo &gt; Agente &gt; Listino).
                  </FieldHint>
                </Label>
                <PercentInput value={formData.commission} onChange={(v) => setFormData({ ...formData, commission: v })} data-testid="pricelist-commission-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-700 flex items-center gap-1">
                  {t.markup}
                  <FieldHint>
                    <strong>Ricarico (margine) del listino</strong> applicato come margine sul prezzo di vendita (non additivo). Esempio: <code>base 13,10€</code> con markup 5% → <code>13,10 / (1 - 0,05) = 13,79€</code>.
                  </FieldHint>
                </Label>
                <PercentInput value={formData.markup} onChange={(v) => setFormData({ ...formData, markup: v })} data-testid="pricelist-markup-input" />
              </div>
            </div>
            <div className="space-y-2 p-3 bg-[#C5A059]/10 rounded border border-[#C5A059]/30">
              <Label className="text-stone-700 flex items-center gap-2 font-semibold">
                🏷️ Sconto Listino
                <FieldHint>
                  <strong>Sconto percentuale</strong> applicato automaticamente a <em>tutti</em> i prodotti di questo listino al momento dell'ordine. Diversamente dal markup, riduce il prezzo finale. Es: 5 = sconto 5% su tutta la lista.
                </FieldHint>
              </Label>
              <p className="text-xs text-stone-500">Sconto % applicato a tutti i prodotti di questo listino (es. digita <code>5</code> per 5%)</p>
              <PercentInput value={formData.discount} onChange={(v) => setFormData({ ...formData, discount: v })} data-testid="pricelist-discount-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-stone-700 flex items-center gap-2">
                📝 Note del listino
                <span className="text-xs text-stone-400 font-normal">(appariranno sopra la tabella nel PDF)</span>
              </Label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Es: Minimo d'ordine 500 kg — Prezzi franco partenza — Validità 7 giorni"
                rows={3}
                className="w-full px-3 py-2 text-sm bg-white border border-stone-300 rounded-md focus:outline-none focus:border-[#8A1C2A] focus:ring-2 focus:ring-[#8A1C2A]/20"
                data-testid="pricelist-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="border-stone-300 text-stone-700">{t.cancel}</Button>
            <Button onClick={handleSave} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="pricelist-save-btn">{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <QuickPricelistDialog open={quick !== null} pricelist={quick || null} onClose={() => setQuick(null)} onSaved={fetchTypes} />
    </div>
  );
};


export default PricelistTypesManagement;

